import * as THREE from "three";

/**
 * Frame 002 · Production scene.
 *
 * "Perspective reveals that a relationship exists;
 *  rotation reveals how the relationship is constructed."
 *
 * Two still objects in black space. The camera performs four acts
 * in strict order:
 *   1. withdraw — reveal that a relationship exists
 *   2. hold     — allow the distance to be experienced
 *   3. rotate   — small arc, examining the relationship
 *   4. settle   — final hold
 *
 * The objects do not move. Ever.
 * See /app/memory/FRAME_002_SPECIFICATION.md.
 */


// -- Production defaults --------------------------------------------------

export const FRAME_002_DEFAULTS = {
  // Act 1 — Withdraw.
  cameraStartZ: 0.55,
  cameraEndZ: 2.90,
  cameraDuration: 12.0,          // seconds from start to end
  easing: "smoothstep",          // "linear" | "smoothstep" | "cubic"

  // Act 2 — Hold the distance. Brief. The observer needs enough
  // time to register the gap before the camera begins to examine it.
  distanceHoldDuration: 2.5,     // seconds

  // Act 3 — Rotate. Small arc, restrained, jeweler under light.
  //   Never a full orbit, never symmetric turntable.
  rotationArc: 0.20,             // radians (~11.5°) — small on purpose
  rotationDuration: 4.5,         // seconds

  // Act 4 — Settle. The camera stops. It does not loop.
  holdDuration: 5.0,             // seconds

  // Object arrangement.
  objectSeparation: 1.0,         // scales base positions from origin
  objectScale: 0.13,             // outer crystal shell radius

  // Object appearance — restrained, localized, not atmospheric.
  lightIntensity: 2.6,
  lightFalloff: 0.05,
  materialTransmission: 0.22,
};


// Fixed base positions. Object B lies outside the horizontal frustum
// at cameraStartZ = 0.55 with fov 30°, aspect ~1.6.
const BASE_POSITIONS = [
  new THREE.Vector3( 0.00,  0.00, -0.35),   // A — dominates the initial frame
  new THREE.Vector3( 1.35,  0.22, -1.55),   // B — outside the frame at start
];

// Rotation orbits around the composition's centroid. This makes the
// small arc feel like the jeweler tilting the stone under the light,
// not the camera orbiting a hero object.
const CENTROID = new THREE.Vector3(
  (BASE_POSITIONS[0].x + BASE_POSITIONS[1].x) * 0.5,
  (BASE_POSITIONS[0].y + BASE_POSITIONS[1].y) * 0.5,
  (BASE_POSITIONS[0].z + BASE_POSITIONS[1].z) * 0.5,
);


function ease(name, t) {
  t = Math.max(0, Math.min(1, t));
  if (name === "linear") return t;
  if (name === "cubic") return t * t * (3 - 2 * t) * (t * t * (3 - 2 * t)); // slightly firmer
  return t * t * (3 - 2 * t); // smoothstep
}


export function createFrame002Scene({ container, initialParams, onProgress }) {
  const width = container.clientWidth || window.innerWidth;
  const height = container.clientHeight || window.innerHeight;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000); // black — visually neutral

  const camera = new THREE.PerspectiveCamera(30, width / height, 0.01, 200);
  camera.position.set(0, 0, initialParams.cameraStartZ);
  camera.lookAt(0, 0, -1);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    preserveDrawingBuffer: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  container.appendChild(renderer.domElement);

  // A dark neutral environment so the frosted shells have something
  // to refract. Kept nearly black — no atmosphere.
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new THREE.Scene();
  envScene.background = new THREE.Color(0x040404);
  const envTex = pmrem.fromScene(envScene, 0.04).texture;
  scene.environment = envTex;


  // -- Object construction --------------------------------------------
  //
  // Each object is a three-layer thing:
  //   core   — the emissive presence
  //   shell  — the frosted material that contains it
  //   halo   — a very quiet local glow just outside the shell
  //
  // No object has any per-frame behavior. They are still.

  const OBJECT_COLOR = 0xf3ead6;   // warm off-white, identical across objects

  const coreGeo = new THREE.SphereGeometry(1, 24, 24);
  const shellGeo = new THREE.SphereGeometry(1, 48, 48);
  const haloGeo = new THREE.SphereGeometry(1, 24, 24);

  const objects = BASE_POSITIONS.map(() => {
    const group = new THREE.Group();

    const coreMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(OBJECT_COLOR),
      toneMapped: false,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    group.add(core);

    const shellMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      roughness: 0.72,
      metalness: 0,
      transmission: initialParams.materialTransmission,
      thickness: initialParams.objectScale * 2.0,
      ior: 1.42,
      attenuationColor: new THREE.Color(OBJECT_COLOR),
      attenuationDistance: 0.35,
      transparent: false,
    });
    const shell = new THREE.Mesh(shellGeo, shellMat);
    group.add(shell);

    const haloMat = new THREE.MeshBasicMaterial({
      color: new THREE.Color(OBJECT_COLOR),
      transparent: true,
      opacity: 0.03,
      depthWrite: false,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
    });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    group.add(halo);

    scene.add(group);
    return { group, core, shell, halo, coreMat, shellMat, haloMat };
  });


  let currentParams = { ...initialParams };


  function applyStaticParams(p) {
    objects.forEach((o, i) => {
      const base = BASE_POSITIONS[i];
      o.group.position.set(
        base.x * p.objectSeparation,
        base.y * p.objectSeparation,
        base.z * p.objectSeparation,
      );
      o.core.scale.setScalar(p.objectScale * 0.55);
      o.shell.scale.setScalar(p.objectScale);
      o.halo.scale.setScalar(p.objectScale * (1.35 + p.lightFalloff * 4));

      // Brightness of the emissive core.
      o.coreMat.color.set(OBJECT_COLOR);
      o.coreMat.color.multiplyScalar(p.lightIntensity);

      // Shell material.
      o.shellMat.transmission = p.materialTransmission;
      o.shellMat.thickness = p.objectScale * 2.0;
      o.shellMat.needsUpdate = true;

      // Halo — kept quiet. Never becomes atmosphere.
      o.haloMat.opacity = 0.012 + p.lightFalloff * 0.06;
    });
  }
  applyStaticParams(currentParams);


  // -- Playback --------------------------------------------------------
  //
  // Four acts in strict order:
  //   "withdrawing"      — cameraStartZ → cameraEndZ
  //   "holding-distance" — camera at cameraEndZ, brief pause
  //   "rotating"         — small arc around the composition's centroid
  //   "settling"         — final hold after the rotation completes
  //   "done"             — playback has finished; camera does not loop
  //
  // The order is not adjustable. If the observer sees rotation
  // before they have felt the distance, the rotation reads as motion
  // instead of examination.

  let playState = "idle"; // "idle" | "withdrawing" | "holding-distance" | "rotating" | "settling" | "done"
  let phaseT = 0;          // seconds elapsed within the current phase

  // Helper — position the camera at the end of the withdraw phase,
  // rotated by `arcT` fraction of the full rotationArc.
  function placeCameraForRotation(p, arcT) {
    const theta = p.rotationArc * arcT;
    // Rotate around Y-axis through the centroid.
    const dx = 0 - CENTROID.x;
    const dz = p.cameraEndZ - CENTROID.z;
    const cosR = Math.cos(theta);
    const sinR = Math.sin(theta);
    const newX = CENTROID.x + dx * cosR - dz * sinR;
    const newZ = CENTROID.z + dx * sinR + dz * cosR;
    camera.position.set(newX, 0, newZ);
    // Camera still looks at the centroid — the composition stays
    // roughly centered while the depth-relationship among its parts
    // is revealed.
    camera.lookAt(CENTROID.x, CENTROID.y, CENTROID.z);
  }

  function startPlayback() {
    playState = "withdrawing";
    phaseT = 0;
    camera.position.set(0, 0, currentParams.cameraStartZ);
    camera.lookAt(0, 0, -1);
  }
  function stopPlayback() {
    playState = "idle";
    phaseT = 0;
    camera.position.set(0, 0, currentParams.cameraStartZ);
    camera.lookAt(0, 0, -1);
  }
  function jumpToEnd() {
    playState = "done";
    phaseT = 0;
    placeCameraForRotation(currentParams, 1);
  }


  // -- Frame loop ------------------------------------------------------

  const clock = new THREE.Clock();
  let running = true;

  function loop() {
    if (!running) return;
    requestAnimationFrame(loop);
    const dt = clock.getDelta();
    const p = currentParams;

    // Advance the phase timer.
    if (
      playState === "withdrawing" ||
      playState === "holding-distance" ||
      playState === "rotating" ||
      playState === "settling"
    ) {
      phaseT += dt;
    }

    // Transition and position.
    if (playState === "withdrawing") {
      const duration = Math.max(0.5, p.cameraDuration);
      const t = Math.min(1, phaseT / duration);
      const eased = ease(p.easing, t);
      const z = p.cameraStartZ + (p.cameraEndZ - p.cameraStartZ) * eased;
      camera.position.set(0, 0, z);
      camera.lookAt(0, 0, -1);
      if (t >= 1) {
        playState = "holding-distance";
        phaseT = 0;
      }
    } else if (playState === "holding-distance") {
      // Camera stays at the withdrawn position, looking straight in.
      camera.position.set(0, 0, p.cameraEndZ);
      camera.lookAt(0, 0, -1);
      if (phaseT >= p.distanceHoldDuration) {
        playState = "rotating";
        phaseT = 0;
      }
    } else if (playState === "rotating") {
      const duration = Math.max(0.3, p.rotationDuration);
      const t = Math.min(1, phaseT / duration);
      // Smoothstep for a jeweler's easing — no sudden start, no
      // sudden stop.
      const eased = t * t * (3 - 2 * t);
      placeCameraForRotation(p, eased);
      if (t >= 1) {
        playState = "settling";
        phaseT = 0;
      }
    } else if (playState === "settling") {
      // Camera stays at the fully-rotated position.
      placeCameraForRotation(p, 1);
      if (phaseT >= p.holdDuration) {
        playState = "done";
      }
    } else if (playState === "done") {
      // Camera does not loop; keep it at the final position.
      placeCameraForRotation(p, 1);
    } else {
      // "idle"
      camera.position.set(0, 0, p.cameraStartZ);
      camera.lookAt(0, 0, -1);
    }

    if (onProgress) {
      const phaseDurations = {
        "idle": 0,
        "withdrawing": p.cameraDuration,
        "holding-distance": p.distanceHoldDuration,
        "rotating": p.rotationDuration,
        "settling": p.holdDuration,
        "done": 0,
      };
      const phaseDur = phaseDurations[playState] || 0;
      onProgress({
        state: playState,
        phaseT,
        phaseDuration: phaseDur,
        phaseFraction: phaseDur > 0 ? Math.min(1, phaseT / phaseDur) : (playState === "done" ? 1 : 0),
      });
    }

    renderer.render(scene, camera);
  }
  loop();


  function onResize() {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }
  window.addEventListener("resize", onResize);


  function screenshot({ presetName = "unsaved", scale = 2 } = {}) {
    const oldSize = renderer.getSize(new THREE.Vector2());
    const w = Math.round(oldSize.x * scale);
    const h = Math.round(oldSize.y * scale);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);

    const dataUrl = renderer.domElement.toDataURL("image/png");
    renderer.setSize(oldSize.x, oldSize.y, false);
    camera.aspect = oldSize.x / oldSize.y;
    camera.updateProjectionMatrix();

    const timestamp = new Date().toISOString();
    const meta = {
      frame: "002",
      timestamp,
      preset: presetName,
      cameraPosition: camera.position.toArray(),
      params: { ...currentParams },
      playState,
      phaseT,
    };

    const stamp = timestamp.replace(/[:.]/g, "-");
    const pngLink = document.createElement("a");
    pngLink.href = dataUrl;
    pngLink.download = `frame002_${stamp}.png`;
    document.body.appendChild(pngLink);
    pngLink.click();
    document.body.removeChild(pngLink);

    const blob = new Blob([JSON.stringify(meta, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const jsonLink = document.createElement("a");
    jsonLink.href = url;
    jsonLink.download = `frame002_${stamp}.json`;
    document.body.appendChild(jsonLink);
    jsonLink.click();
    document.body.removeChild(jsonLink);
    URL.revokeObjectURL(url);
  }


  function dispose() {
    running = false;
    window.removeEventListener("resize", onResize);
    if (renderer.domElement.parentNode === container) {
      container.removeChild(renderer.domElement);
    }
    coreGeo.dispose();
    shellGeo.dispose();
    haloGeo.dispose();
    objects.forEach((o) => {
      o.coreMat.dispose();
      o.shellMat.dispose();
      o.haloMat.dispose();
    });
    envTex.dispose();
    pmrem.dispose();
    renderer.dispose();
  }


  return {
    setParams(p) {
      currentParams = { ...p };
      applyStaticParams(currentParams);
    },
    play: startPlayback,
    stop: stopPlayback,
    jumpToEnd,
    getState() {
      return { state: playState, phaseT };
    },
    screenshot,
    dispose,
  };
}
